import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  Users, 
  Package, 
  Calculator, 
  BarChart3, 
  Shield, 
  Zap, 
  Globe,
  Check,
  ArrowRight,
  Menu,
  X
} from "lucide-react";
import { useState } from "react";

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: FileText,
      title: "Professional Invoices",
      description: "Create stunning invoices with 6 modern templates. Add your logo, customize terms, and send directly to clients."
    },
    {
      icon: Calculator,
      title: "Smart Tax Management",
      description: "Handle complex tax scenarios including per-unit taxes like KY Vape Tax. Automatic calculations, zero errors."
    },
    {
      icon: Package,
      title: "Inventory Tracking",
      description: "Real-time stock levels, low-stock alerts, and automatic inventory updates when invoices are sent."
    },
    {
      icon: Users,
      title: "Customer Management",
      description: "Complete customer profiles with payment history, outstanding balances, and custom fields."
    },
    {
      icon: BarChart3,
      title: "Powerful Reports",
      description: "Sales summaries, tax reports, customer analytics, and item performance—all exportable to CSV/PDF."
    },
    {
      icon: Shield,
      title: "Secure & Reliable",
      description: "Bank-level encryption, automatic backups, and role-based access control for your team."
    }
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: "Free",
      description: "Perfect for freelancers",
      features: ["50 invoices/month", "1 organization", "3 invoice templates", "Basic reports", "Email support"],
      cta: "Get Started Free",
      popular: false
    },
    {
      name: "Professional",
      price: "$29",
      period: "/month",
      description: "For growing businesses",
      features: ["Unlimited invoices", "3 organizations", "All 6 templates", "Advanced reports", "Inventory tracking", "Priority support"],
      cta: "Start Free Trial",
      popular: true
    },
    {
      name: "Enterprise",
      price: "$99",
      period: "/month",
      description: "For large teams",
      features: ["Everything in Pro", "Unlimited organizations", "API access", "Custom integrations", "Dedicated account manager", "SLA guarantee"],
      cta: "Contact Sales",
      popular: false
    }
  ];

  const faqs = [
    {
      q: "How does per-unit tax work?",
      a: "Our system supports fixed taxes per unit (like KY Vape Tax at $1.50/unit). When you add items to an invoice, the tax is automatically calculated based on quantity and unit type (pcs, box, case)."
    },
    {
      q: "Can I customize invoice templates?",
      a: "Yes! We offer 6 professionally designed templates (Modern, Classic, Minimal, Bold, Elegant, Corporate). Each includes your logo, custom colors, and can be tailored to your brand."
    },
    {
      q: "Is my data secure?",
      a: "Absolutely. We use bank-level 256-bit SSL encryption, daily backups, and your data is stored in SOC 2 compliant data centers. You own your data—export anytime."
    },
    {
      q: "Do you support multiple currencies?",
      a: "Yes, you can set a default currency per organization and override it per invoice. We support USD, EUR, GBP, CAD, and 50+ other currencies."
    },
    {
      q: "Can I track payments?",
      a: "Record payments against invoices with multiple payment methods (cash, card, bank transfer, ACH, check). Track partial payments, outstanding balances, and payment history."
    },
    {
      q: "What payment methods do you support?",
      a: "We support Stripe card payments for online checkout, plus manual recording of Cash, Check, and ACH bank transfers. Your customers can pay via a shareable payment link."
    },
    {
      q: "Can I manage multiple businesses?",
      a: "Yes! Create multiple organizations under one account, each with its own logo, settings, items, customers, and invoices. Switch between them instantly."
    },
    {
      q: "Do you offer team collaboration?",
      a: "Invite staff members via email with role-based permissions. Admins manage everything, while staff can create invoices and manage customers within their assigned organization."
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const teamMembers = [
    { name: "Sarah Chen", role: "CEO & Co-founder", desc: "15+ years in fintech. Previously VP at Square." },
    { name: "Michael Torres", role: "CTO & Co-founder", desc: "Former engineering lead at Stripe." },
    { name: "Priya Sharma", role: "Head of Product", desc: "Product leader with deep SMB expertise." },
    { name: "David Kim", role: "Head of Customer Success", desc: "Passionate about helping businesses grow." }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-zinc-200/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-semibold text-lg">InvoiceHub</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-zinc-600 hover:text-primary transition-colors">Features</a>
              <a href="#pricing" className="text-sm text-zinc-600 hover:text-primary transition-colors">Pricing</a>
              <a href="#about" className="text-sm text-zinc-600 hover:text-primary transition-colors">About</a>
              <a href="#contact" className="text-sm text-zinc-600 hover:text-primary transition-colors">Contact</a>
              <a href="#faq" className="text-sm text-zinc-600 hover:text-primary transition-colors">FAQ</a>
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="nav-login-btn">Log In</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" data-testid="nav-signup-btn">Start Free</Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile Nav */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-zinc-200">
              <div className="flex flex-col gap-4">
                <a href="#features" className="text-sm text-zinc-600">Features</a>
                <a href="#pricing" className="text-sm text-zinc-600">Pricing</a>
                <a href="#about" className="text-sm text-zinc-600">About</a>
                <a href="#contact" className="text-sm text-zinc-600">Contact</a>
                <a href="#faq" className="text-sm text-zinc-600">FAQ</a>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="w-full">Log In</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="w-full">Start Free</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/18587788/pexels-photo-18587788.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
            alt="Modern architecture"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-transparent"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4 opacity-0 animate-fade-in stagger-1">
              Invoice Management Simplified
            </p>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-zinc-900 mb-6 opacity-0 animate-fade-in stagger-2">
              Professional invoicing for <span className="text-primary font-medium">modern businesses</span>
            </h1>
            <p className="text-lg text-zinc-600 mb-8 leading-relaxed opacity-0 animate-fade-in stagger-3">
              Create, send, and track invoices in minutes. Handle complex taxes, manage inventory, and get paid faster—all in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in stagger-4">
              <Link to="/register">
                <Button size="lg" className="w-full sm:w-auto gap-2 hover:-translate-y-0.5 transition-transform" data-testid="hero-cta-btn">
                  Start Free Trial <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <a href="#features">
                <Button variant="outline" size="lg" className="w-full sm:w-auto">
                  See Features
                </Button>
              </a>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-zinc-500 opacity-0 animate-fade-in stagger-5">
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-primary" /> No credit card</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-primary" /> 14-day free trial</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-primary" /> Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">Features</p>
            <h2 className="font-outfit text-3xl sm:text-4xl tracking-tight font-medium text-zinc-900 mb-4">
              Everything you need to get paid
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              From creating professional invoices to tracking payments and managing inventory—InvoiceHub has you covered.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white border border-zinc-200 p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-accent flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-outfit text-xl font-medium text-zinc-900 mb-3">{feature.title}</h3>
                <p className="text-zinc-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Feature Highlight */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">Smart Tax Handling</p>
              <h2 className="font-outfit text-3xl sm:text-4xl tracking-tight font-medium text-zinc-900 mb-6">
                Complex taxes made simple
              </h2>
              <p className="text-zinc-600 mb-8 leading-relaxed">
                Whether it's percentage-based sales tax or fixed per-unit taxes like the Kentucky Vape Tax ($1.50/unit), our system handles it automatically. Set up your tax rules once, apply them to items, and watch the calculations happen in real-time.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-accent flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-zinc-900">Percentage & Fixed Rate Taxes</h4>
                    <p className="text-sm text-zinc-600">Apply 6% sales tax or $1.50 per unit—your choice.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-accent flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-zinc-900">Unit-Aware Calculations</h4>
                    <p className="text-sm text-zinc-600">Tax by piece, box, case, or hourly rate.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-accent flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-zinc-900">Detailed Tax Reports</h4>
                    <p className="text-sm text-zinc-600">Export tax collected by rule, jurisdiction, or period.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-zinc-50 border border-zinc-200 p-8">
              <h4 className="font-outfit text-lg font-medium mb-6">Example: KY Vape Tax</h4>
              <div className="space-y-4 text-sm">
                <div className="flex justify-between pb-2 border-b border-zinc-200">
                  <span className="text-zinc-600">Vape Product × 5 pcs</span>
                  <span>$25.00</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-zinc-200">
                  <span className="text-zinc-600">Tax ($1.50/unit × 5)</span>
                  <span className="text-primary">$7.50</span>
                </div>
                <div className="flex justify-between font-medium text-lg">
                  <span>Total</span>
                  <span>$32.50</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">Pricing</p>
            <h2 className="font-outfit text-3xl sm:text-4xl tracking-tight font-medium text-zinc-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              Start free, upgrade when you need more. No hidden fees, no surprises.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index}
                className={`bg-white border p-8 relative ${plan.popular ? 'border-primary shadow-lg' : 'border-zinc-200'}`}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs px-3 py-1 font-medium">
                    Most Popular
                  </div>
                )}
                <h3 className="font-outfit text-xl font-medium text-zinc-900 mb-2">{plan.name}</h3>
                <p className="text-sm text-zinc-500 mb-4">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-outfit font-light">{plan.price}</span>
                  {plan.period && <span className="text-zinc-500">{plan.period}</span>}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-zinc-600">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button 
                    variant={plan.popular ? "default" : "outline"} 
                    className="w-full"
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">About Us</p>
              <h2 className="font-outfit text-3xl sm:text-4xl tracking-tight font-medium text-zinc-900 mb-6">
                Built by business owners, for business owners
              </h2>
              <p className="text-zinc-600 mb-6 leading-relaxed">
                InvoiceHub was born from a simple frustration: existing invoicing tools couldn't handle the complexity of real businesses. Whether it's per-unit excise taxes, multi-organization management, or custom inventory tracking—we built the tool we wished existed.
              </p>
              <p className="text-zinc-600 mb-8 leading-relaxed">
                Today, InvoiceHub serves thousands of businesses across industries like retail, wholesale, vape distribution, consulting, and professional services. Our mission is to make financial operations effortless so you can focus on growth.
              </p>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-outfit font-light text-primary">5K+</div>
                  <p className="text-xs text-zinc-500 mt-1">Active Businesses</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-outfit font-light text-primary">$2B+</div>
                  <p className="text-xs text-zinc-500 mt-1">Invoices Processed</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-outfit font-light text-primary">99.9%</div>
                  <p className="text-xs text-zinc-500 mt-1">Uptime SLA</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {teamMembers.map((member, i) => (
                <div key={i} className="bg-zinc-50 border border-zinc-200 p-6 hover:shadow-md transition-shadow" data-testid={`team-member-${i}`}>
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <span className="text-lg font-outfit font-medium text-primary">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <h4 className="font-medium text-zinc-900 text-sm">{member.name}</h4>
                  <p className="text-xs text-primary font-medium mt-0.5">{member.role}</p>
                  <p className="text-xs text-zinc-500 mt-2">{member.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">Contact Us</p>
              <h2 className="font-outfit text-3xl sm:text-4xl tracking-tight font-medium text-zinc-900 mb-6">
                Get in touch
              </h2>
              <p className="text-zinc-600 mb-8 leading-relaxed">
                Have questions about InvoiceHub? Want to schedule a demo? Our team is here to help you find the right solution for your business.
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <Globe className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-zinc-900">Email Us</h4>
                    <p className="text-sm text-zinc-600">support@invoicehub.com</p>
                    <p className="text-sm text-zinc-600">sales@invoicehub.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-zinc-900">Live Chat</h4>
                    <p className="text-sm text-zinc-600">Available Mon-Fri, 9am-6pm EST</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-zinc-900">Enterprise Support</h4>
                    <p className="text-sm text-zinc-600">Dedicated account manager for Enterprise plans</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <form onSubmit={(e) => { e.preventDefault(); alert('Thank you! We will get back to you shortly.'); }} className="bg-white border border-zinc-200 p-8 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1.5">First Name</label>
                    <input type="text" placeholder="John" className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" required data-testid="contact-first-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1.5">Last Name</label>
                    <input type="text" placeholder="Doe" className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" required data-testid="contact-last-name" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1.5">Work Email</label>
                  <input type="email" placeholder="john@company.com" className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" required data-testid="contact-email" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1.5">Company</label>
                  <input type="text" placeholder="Company Inc." className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" data-testid="contact-company" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1.5">Message</label>
                  <textarea rows="4" placeholder="Tell us about your needs..." className="w-full px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none" required data-testid="contact-message"></textarea>
                </div>
                <Button type="submit" className="w-full" data-testid="contact-submit-btn">
                  Send Message
                </Button>
                <p className="text-xs text-center text-zinc-400">We typically respond within 24 hours</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">FAQ</p>
            <h2 className="font-outfit text-3xl sm:text-4xl tracking-tight font-medium text-zinc-900">
              Frequently asked questions
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-zinc-200 overflow-hidden" data-testid={`faq-item-${index}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <h3 className="font-outfit font-medium text-zinc-900 pr-4">{faq.q}</h3>
                  <ArrowRight className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform duration-200 ${openFaq === index ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-sm text-zinc-600 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl tracking-tight font-medium text-white mb-6">
            Ready to streamline your invoicing?
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            Join thousands of businesses that trust InvoiceHub to manage their invoicing. Start your free trial today—no credit card required.
          </p>
          <Link to="/register">
            <Button size="lg" variant="secondary" className="gap-2" data-testid="cta-final-btn">
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-primary flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <span className="font-outfit font-semibold text-lg">InvoiceHub</span>
              </div>
              <p className="text-sm text-zinc-400">
                Professional invoice management for modern businesses.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Sign Up</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li><a href="#about" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li><a href="#faq" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact Support</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-8 text-center text-sm text-zinc-500">
            © {new Date().getFullYear()} InvoiceHub. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
